import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import ReactDataGrid from "@inovua/reactdatagrid-community";
import { filterTypes, generateFilterColumns } from "./helper";
import {
  DynamicColGenerator,
  GenerateActionButtons,
} from "./dynamic-columns-generator";

import {
  selectLeadsRecordsCount,
  setRecordsPerPage,
} from "@/store/slices/lead/crmLeadSlice";
import {
  selectGridColumnOrder,
  updateGridColumnOrder,
} from "@/store/slices/grid/gridStateSlice";
import CircularProgress from "@mui/material/CircularProgress";
import { useRouter } from "next/router";
import { useGridFilters } from "./useGridFilters";
import { camelize } from "@/services/generic.methods";

const gridStyle = {
  minHeight: "calc(100vh - 150px)",
  marginTop: 0,
  boxShadow: "0px 3px 10px 0 rgba(0, 0, 0, 0.08)",
  overflow: "hidden",
};

const getUrlSuffix = (path) => {
  const regex = /\/([^/]+)\/?$/;
  const match = path.match(regex);
  return match ? match[1] : null;
};

export const GridWrapper = ({
  garbageColumnHeaderText = [],
  actionButtons,
  actionButtonsPosition = "end",
  actionButtonsProps = {},
  setLoading,
  loading,
  loadingMessage,
  gridloading,
  selectedData = {},
  handleSelect,
  gridClass,
  refetch = 0,
  clearFilter = 0,
  customGridStyle = {},
  rowHeight = 30,
  recordsLimit = 0,
  activateRowOnFocus = true,
  showActiveRowIndicator = false,
  checkboxOnlyRowSelect = true,
  checkboxColumn = true,
  children,
  defaultVisibleColumns = [],
  rawColumnsData = [],
  dataSource,
  handleVisibleChange = () => {},
  handleColumnOrderChange = () => {},
  customColumnsData = [],
  onRowClick,
  gridType = "remote",
  paginate,
  setPaginate,
  pagination = true,
  emptyText,
  customFields = [],
  groups = [],
  idProperty = "id",
  paginateName = null,
  gridId = null,
  snapFrozenActionColumn = false,
}) => {
  const recordsPerPage = useSelector(selectLeadsRecordsCount);
  const dispatch = useDispatch();
  const savedColumnOrder = useSelector((state) =>
    gridId ? selectGridColumnOrder(gridId)(state) : null
  );
  const [routeSuffix, setRouteSuffix] = useState("leads");
  const { route } = useRouter();
  const [filters, setFilters] = useState({});
  const [showColumnsFilters, setShowColumnsFilters] = useState(true);
  const [cols, setCols] = useState([]);
  const [visibleCols, setVisibleCols] = useState({});
  const [gridRef, setGridRef] = useState(null);
  const frozenWrapRef = useRef(null);
  const userModifiedVisibleColsRef = useRef(false);
  const defaultVisibleColumnsKey = JSON.stringify(defaultVisibleColumns);

  // Replace local columnFilters state with useGridFilters hook
  // Must be called before any useState that depends on its values
  const {
    filterValue,
    sortInfo,
    pagination: persistedPagination,
    defaultFilterValue,
    defaultSortInfo,
    defaultPagination,
    onFilterValueChange,
    onSortInfoChange,
    onSkipChange,
    onLimitChange,
  } = useGridFilters(gridId, cols);

  const [limit, setLimit] = useState(
    recordsLimit || persistedPagination?.limit || recordsPerPage?.[routeSuffix]
  );
  const [skip, setSkip] = useState(persistedPagination?.skip || 0);

  useEffect(() => {
    setRouteSuffix(paginateName || getUrlSuffix(route));
  }, []);

  useEffect(() => {
    const perPage = recordsPerPage?.[paginateName || getUrlSuffix(route)];
    if (perPage && !persistedPagination?.limit) {
      setLimit(perPage);
    }
  }, [recordsPerPage]);

  // Sync persisted pagination to local state when it changes
  useEffect(() => {
    if (persistedPagination) {
      if (persistedPagination.limit) setLimit(persistedPagination.limit);
      if (persistedPagination.skip !== undefined)
        setSkip(persistedPagination.skip);
    }
  }, [persistedPagination?.limit, persistedPagination?.skip]);

  const handleLimitOnChange = (newLimit) => {
    setLimit(newLimit);
    dispatch(setRecordsPerPage({ [routeSuffix]: newLimit }));
    onLimitChange(newLimit);
  };

  const handleSkipChange = (newSkip) => {
    setSkip(newSkip);
    onSkipChange(newSkip);
  };

  const renderColumnContextMenu = useCallback((menuProps, { cellProps }) => {
    menuProps.items = menuProps.items.splice(0, 8);
  }, []);

  const localHandleColumnOrderChange = useCallback(
    (columnOrder) => {
      if (gridId) {
        dispatch(updateGridColumnOrder({ gridId, columnOrder }));
      }
      if (typeof handleColumnOrderChange === "function") {
        handleColumnOrderChange(columnOrder);
      }
    },
    [gridId, dispatch, handleColumnOrderChange]
  );

  const getCols = async () => {
    let columnsMeta = rawColumnsData.map((item) => {
      let customItem = customColumnsData.find(
        (cItem) => cItem.columnName === item.columnName
      );
      return {
        ...item,
        ...customItem,
      };
    });

    columnsMeta = columnsMeta
      .filter((x) => !x.hidden)
      .sort((a, b) => {
        if (defaultVisibleColumns && defaultVisibleColumns.length) {
          const aIndex = defaultVisibleColumns.findIndex(
            (colName) =>
              colName.toLowerCase() === camelize(a.columnName).toLowerCase()
          );
          const bIndex = defaultVisibleColumns.findIndex(
            (colName) =>
              colName.toLowerCase() === camelize(b.columnName).toLowerCase()
          );
          if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
          if (aIndex !== -1) return -1;
          if (bIndex !== -1) return 1;
        }
        return (a.order || Infinity) - (b.order || Infinity);
      });
    let generatedColumns = DynamicColGenerator(
      columnsMeta,
      defaultVisibleColumns,
      garbageColumnHeaderText,
      onRowClick,
      customFields
    );
    setCols(generatedColumns);
    // setColumnFilters(generateFilterColumns(generatedColumns));
    if (!userModifiedVisibleColsRef.current) {
      const obj = {};
      generatedColumns.forEach((gc) => {
        obj[gc.name] = {
          visible: gc.visible,
          header: gc?.headerProps?.headerText,
        };
      });
      setVisibleCols(obj);
    }
  };

  const handleSetVisibleCols = useCallback((nextVisibleCols) => {
    userModifiedVisibleColsRef.current = true;
    setVisibleCols(nextVisibleCols);
  }, []);

  useEffect(() => {
    onReload();
    if (Object.keys(visibleCols).length) handleVisibleChange(visibleCols);
  }, [JSON.stringify(visibleCols)]);

  useEffect(() => {
    getCols();
  }, [
    rawColumnsData,
    defaultVisibleColumnsKey,
    customFields.length,
    onRowClick,
  ]);

  const onReload = () => {
    if (gridRef && gridRef.current) {
      gridRef.current.reload();
    }
  };

  // Freeze the actions column. The community grid scrolls horizontally via CSS
  // transform (no native scrollport) and the Pro `locked` API is render-gated,
  // so we keep the actions cells pinned by translating them to counter the
  // horizontal scroll. A single CSS variable on the wrapper drives every
  // actions cell (header + body), so virtualized rows stay in sync for free.
  const updateFrozenOffset = useCallback(() => {
    const instance = gridRef?.current;
    const wrap = frozenWrapRef.current;
    if (!instance || !wrap) return;
    const max =
      typeof instance.getScrollLeftMax === "function"
        ? instance.getScrollLeftMax()
        : 0;
    const left =
      typeof instance.getScrollLeft === "function"
        ? instance.getScrollLeft()
        : 0;
    let tx = Math.round(left - max);
    // Lists grid only. scrollLeftMax stays larger than the rendered columns
    // after a zoom change, which slides the actions column over its neighbor.
    // Pin from the header cells' layout widths instead — same CSS pixels as
    // the grid translate, and 0 when the columns still fit.
    if (snapFrozenActionColumn) {
      const header = wrap.querySelector(".InovuaReactDataGrid__header");
      const clip = wrap.querySelector(".InovuaReactDataGrid__column-layout");
      const cells = header?.querySelectorAll(
        ":scope > .InovuaReactDataGrid__column-header__resize-wrapper"
      );
      if (cells?.length && clip?.clientWidth > 0) {
        let contentWidth = 0;
        cells.forEach((cell) => {
          contentWidth += cell.offsetWidth;
        });
        const stickyMax = Math.max(0, contentWidth - clip.clientWidth);
        tx = Math.round(left - stickyMax);
      }
    }
    wrap.style.setProperty("--frozen-tx", `${tx}px`);
  }, [gridRef, snapFrozenActionColumn]);

  useEffect(() => {
    let innerRaf = 0;
    const raf = requestAnimationFrame(() => {
      updateFrozenOffset();
      // Column widths settle a frame after the grid handles zoom/resize.
      innerRaf = requestAnimationFrame(updateFrozenOffset);
    });
    window.addEventListener("resize", updateFrozenOffset);
    const viewport = snapFrozenActionColumn ? window.visualViewport : null;
    viewport?.addEventListener("resize", updateFrozenOffset);
    const clip = snapFrozenActionColumn
      ? frozenWrapRef.current?.querySelector(".InovuaReactDataGrid__column-layout")
      : null;
    const observer =
      clip && new ResizeObserver(() => updateFrozenOffset());
    if (clip && observer) observer.observe(clip);
    return () => {
      cancelAnimationFrame(raf);
      cancelAnimationFrame(innerRaf);
      window.removeEventListener("resize", updateFrozenOffset);
      viewport?.removeEventListener("resize", updateFrozenOffset);
      observer?.disconnect();
    };
  }, [updateFrozenOffset, snapFrozenActionColumn, JSON.stringify(visibleCols), cols.length, gridloading]);

  // clearFilter now resets via onFilterValueChange so Redux is also cleared
  useEffect(() => {
    if (clearFilter) {
      onFilterValueChange(generateFilterColumns(cols));
    }
  }, [clearFilter]);

  useEffect(() => {
    if (refetch) {
      onReload();
    }
  }, [refetch]);

  const getColumns = () => {
    let columnsFiltered = [
      ...cols.map((c) => ({ ...c, visible: visibleCols[c.name].visible })),
    ];
    let actions = actionButtons
      ? [
          GenerateActionButtons(
            actionButtons,
            actionButtonsProps,
            actionButtonsPosition
          ),
        ]
      : [];

    if (actionButtonsPosition === "start") {
      columnsFiltered.splice(0, 0, ...actions);
    } else {
      columnsFiltered.push(...actions);
    }

    return columnsFiltered;
  };

  const getColumnOrder = () => {
    if (!savedColumnOrder?.length) return undefined;
    const withoutActions = savedColumnOrder.filter(
      (name) => name !== "actions"
    );
    if (!actionButtons) return withoutActions;
    return actionButtonsPosition === "start"
      ? ["actions", ...withoutActions]
      : [...withoutActions, "actions"];
  };
  const columnOrder = getColumnOrder();

  const sharedGridProps = {
    emptyText,
    checkboxColumn,
    checkboxOnlyRowSelect,
    onReady: setGridRef,
    idProperty,
    style: { ...gridStyle, ...customGridStyle },
    className: gridClass || "",
    virtualizeColumns: false,
    columns: getColumns(),
    limit,
    skip: skip || defaultPagination?.skip,
    defaultLimit: limit,
    defaultSkip: skip || defaultPagination?.skip,
    onLimitChange: handleLimitOnChange,
    onSkipChange: handleSkipChange,
    pagination,
    rowHeight,
    onLoadingChange: setLoading,
    filterTypes,
    showHoverRows: false,
    showCellBorders: false,
    showColumnMenuTool: true,
    loadingText: loadingMessage,
    renderColumnContextMenu,
    onSelectionChange: handleSelect,
    selected:
      selectedData && Object.keys(selectedData).length
        ? Object.keys(selectedData).reduce((acc, key) => {
            acc[key] = true;
            return acc;
          }, {})
        : false,
    enableSelection: true,
    activateRowOnFocus,
    showActiveRowIndicator,
    ...(onRowClick ? { rowClassName: "clickable-grid-row" } : {}),
    groups,
    filterValue: filterValue || defaultFilterValue,
    defaultFilterValue,
    onFilterValueChange,
    sortInfo: sortInfo || defaultSortInfo,
    defaultSortInfo,
    onSortInfoChange,
    onScroll: updateFrozenOffset,
    ...(columnOrder?.length ? { columnOrder } : {}),
    onColumnOrderChange: localHandleColumnOrderChange,
  };

  return (
    <>
      {Object.keys(cols).length && !loading ? (
        <>
          {children &&
            children({
              visibleCols,
              setVisibleCols: handleSetVisibleCols,
              filters,
              setFilters,
              showColumnsFilters,
              setShowColumnsFilters,
            })}
          <div ref={frozenWrapRef} className="frozen-actions-grid-wrap">
            {gridType === "remote" ? (
              <ReactDataGrid
                {...sharedGridProps}
                loading={gridloading}
                dataSource={dataSource}
                onSortInfoChange={(e) => {
                  onSortInfoChange(e);
                  setTimeout(() => onReload(), 0);
                }}
                onSkipChange={(e) => {
                  handleSkipChange(e);
                }}
                onLimitChange={(e) => {
                  handleLimitOnChange(e);
                  setTimeout(() => onReload(), 0);
                }}
                onColumnOrderChange={(columnOrder) => {
                  const newOrder = columnOrder.filter((name) =>
                    cols.some((c) => c.name === name)
                  );
                  if (typeof localHandleColumnOrderChange === "function") {
                    localHandleColumnOrderChange(newOrder);
                  }
                }}
              />
            ) : (
              <ReactDataGrid
                {...sharedGridProps}
                loading={false}
                dataSource={Promise.resolve(dataSource)}
                onLimitChange={(e) => {
                  handleLimitOnChange(e);
                  setPaginate((prev) => ({ ...prev, limit: e }));
                }}
                onFilterValueChange={(e) => {
                  onFilterValueChange(e);
                  setPaginate((prev) => ({ ...prev, filterValue: e }));
                }}
                onSkipChange={(e) => {
                  handleSkipChange(e);
                  setPaginate((prev) => ({ ...prev, skip: e }));
                }}
                onSortInfoChange={(e) => {
                  onSortInfoChange(e);
                  setPaginate((prev) => ({ ...prev, sortInfo: e }));
                }}
                onColumnOrderChange={(columnOrder) => {
                  const newOrder = columnOrder.filter((name) =>
                    cols.some((c) => c.name === name)
                  );
                  if (typeof localHandleColumnOrderChange === "function") {
                    localHandleColumnOrderChange(newOrder);
                  }
                }}
              />
            )}
          </div>
        </>
      ) : loading ? (
        <div style={{ textAlign: "center", height: "100%", marginTop: "1rem" }}>
          <CircularProgress size="60px" />
        </div>
      ) : null}
    </>
  );
};
